import { selectNodeEventProperties } from 'api/data/events/nodeEventProperties';
import type {
  DataNodeCreatedEvent,
  DataNodeUpdatedEvent,
  DataRelationCreatedEvent,
  DataRelationDeletedEvent
} from 'api/data/events/signalREvents';
import type { DecompositionNode } from 'api/data/projects/types';
import type { TreeSyncContext } from 'api/decomposition-tree/treeSyncContext';
import {
  patchNodeInPaginatedPages,
  removeNodeFromPaginatedPages
} from 'api/decomposition-tree/utils/paginatedCacheMutations';
import {
  isNodePinnedUnderNonRootParent,
  patchStickyTopNode,
  pushStickyTopNode,
  removeNodeFromAllStickyTops
} from 'api/decomposition-tree/utils/stickyTopCacheMutations';
import {
  detachNodeFromParentCache,
  findNodeInCache,
  isNodeCreatedThisSession,
  patchHasChildren,
  resetHasChildrenIfEmpty
} from 'api/decomposition-tree/utils/treeCacheLookups';
import { normalizeTreeProposalId } from 'api/decomposition-tree/utils/treeCacheScope';
import { treeQueryKeys } from 'api/decomposition-tree/utils/treeQueryKeys';
import { isAutoIdSettings } from 'api/meta/enums/nodes/properties/EPropertySettingUtils';
import { SELECTED_PROJECT_STORE } from 'state/selectedProject/selectedProjectState';
import { normalizeId } from 'utils/ids';

type ActiveTree = {
  projectId: string;
  templateNodeId: string;
  proposalId: string | undefined;
};

const TREE_ROUTE_PATTERN = /\/projects\/([^/]+)(?:\/proposals\/([^/]+))?\/data\/nodes\/([^/]+)\/tree/;

function parseActiveTree(pathname: string): ActiveTree | undefined {
  const routeMatch = pathname.match(TREE_ROUTE_PATTERN);
  if (!routeMatch) return undefined;
  return {
    projectId: normalizeId(routeMatch[1]),
    proposalId: routeMatch[2] ? normalizeId(routeMatch[2]) : undefined,
    templateNodeId: normalizeId(routeMatch[3])
  };
}

/** Case-insensitive property lookup (SignalR key casing may vary). */
function lookupProperty(properties: Record<string, unknown>, technicalName: string): unknown | undefined {
  if (technicalName in properties) return properties[technicalName];
  const lower = technicalName.toLowerCase();
  for (const [k, v] of Object.entries(properties)) {
    if (k === 'Id' || k === 'id') continue;
    if (k.toLowerCase() === lower) return v;
  }
  return undefined;
}

function extractNodePatch(
  templateNodeId: string,
  properties: Record<string, unknown>
): Partial<Pick<DecompositionNode, 'title' | 'subtitle'>> {
  const templateNode = SELECTED_PROJECT_STORE.getState().template?.nodes.find(
    (node) => normalizeId(node.id) === templateNodeId
  );
  if (!templateNode) return {};

  const patch: Partial<Pick<DecompositionNode, 'title' | 'subtitle'>> = {};

  const autoProp = templateNode.properties.find((p) => isAutoIdSettings(p.settings));
  const primaryProp = templateNode.properties.find((p) => p.id === templateNode.primaryPropertyId);
  const titleProp = primaryProp && !isAutoIdSettings(primaryProp.settings) ? primaryProp : undefined;

  // A schema property absent from `properties` is currently empty — the backend omits empty
  // properties from the bag rather than sending them as '' — so absence must clear the field,
  // not be skipped as "unchanged" (see nodeEventProperties.ts / nodeDetailCache.ts).
  if (autoProp) {
    patch.subtitle = String(lookupProperty(properties, autoProp.technicalName) ?? '');
  }
  if (titleProp) {
    patch.title = String(lookupProperty(properties, titleProp.technicalName) ?? '');
  }

  return patch;
}

function buildStickyNode(
  id: string,
  parentId: string | null,
  templateNodeId: string,
  properties: Record<string, unknown> | null
): DecompositionNode {
  const patch = properties ? extractNodePatch(templateNodeId, properties) : {};
  return {
    id,
    parentId,
    title: patch.title ?? '',
    subtitle: patch.subtitle ?? '',
    hasChildren: false,
    createdThisSession: true
  };
}

function matchesActiveTree(
  activeTree: ActiveTree,
  eventProjectId: string,
  eventTemplateNodeId: string,
  eventProposalId: string | undefined
): boolean {
  if (normalizeId(eventProjectId) !== activeTree.projectId) return false;
  if (normalizeId(eventTemplateNodeId) !== activeTree.templateNodeId) return false;
  return proposalMatches(normalizeTreeProposalId(eventProposalId), activeTree.proposalId);
}

function proposalMatches(eventProposalId: string | undefined, activeProposalId: string | undefined): boolean {
  if (eventProposalId === undefined) return true;
  return eventProposalId === activeProposalId;
}

type DecompositionRelationEvent = {
  ProjectId?: string;
  ProposalId?: string;
  Payload?: Record<string, unknown>;
};

function normalizeRelationPayload(payload: Record<string, unknown> | undefined): {
  TemplateRelationId?: string;
  StartDataNodeId?: string;
  EndDataNodeId?: string;
  Properties?: Record<string, unknown>;
  Proposals?: Record<string, Record<string, unknown>>;
} {
  if (!payload) return {};
  return {
    TemplateRelationId: (payload.TemplateRelationId ?? payload.templateRelationId) as string | undefined,
    StartDataNodeId: (payload.StartDataNodeId ?? payload.startDataNodeId) as string | undefined,
    EndDataNodeId: (payload.EndDataNodeId ?? payload.endDataNodeId) as string | undefined,
    Properties: (payload.Properties ?? payload.properties) as Record<string, unknown> | undefined,
    Proposals: (payload.Proposals ?? payload.proposals) as Record<string, Record<string, unknown>> | undefined
  };
}

function resolveDecompositionRelationSync(
  event: DecompositionRelationEvent,
  context: TreeSyncContext
): { activeTree: ActiveTree; childDataNodeId: string; parentDataNodeId: string } | null {
  const activeTree = parseActiveTree(context.pathname);
  if (!activeTree) return null;

  const eventProjectId = normalizeId(event.ProjectId ?? '');
  if (eventProjectId !== activeTree.projectId) return null;

  if (!proposalMatches(normalizeTreeProposalId(event.ProposalId), activeTree.proposalId)) return null;

  const payload = normalizeRelationPayload(event.Payload);
  const templateRelationId = normalizeId(payload.TemplateRelationId ?? '');
  if (!templateRelationId) return null;

  const template = SELECTED_PROJECT_STORE.getState().template;
  if (!template) return null;

  const ownerNode = template.nodes.find((n) => n.relations.some((r) => normalizeId(r.id) === templateRelationId));
  if (!ownerNode) return null;

  const relation = ownerNode.relations.find((r) => normalizeId(r.id) === templateRelationId);
  if (!relation) return null;

  const candidateIds = [normalizeId(ownerNode.id), normalizeId(relation.targetNodeId)];
  if (!candidateIds.includes(activeTree.templateNodeId)) return null;

  const childDataNodeId = payload.StartDataNodeId;
  const parentDataNodeId = payload.EndDataNodeId;
  if (!childDataNodeId || !parentDataNodeId) return null;

  return { activeTree, childDataNodeId, parentDataNodeId };
}

// Returns true if the live relation event should overwrite the proposal cache.
// Called after resolveDecompositionRelationSync, which already filtered by project/template/proposalMatches.
function shouldLiveRelationOverwriteProposalCache(
  event: DataRelationCreatedEvent | DataRelationDeletedEvent,
  activeProposalId: string | undefined
): boolean {
  // Global live event
  if (activeProposalId === undefined) return true;
  // Proposal event
  if (normalizeTreeProposalId(event.ProposalId)) return true;

  // Global event in proposals list should not overwrite the proposal cache
  // If proposalTouchedNode is true, the proposal cache should not be overwritten
  const proposals = normalizeRelationPayload(event.Payload).Proposals ?? {};
  const proposalTouchedNode = Object.keys(proposals).some((key) => normalizeId(key) === activeProposalId);
  return !proposalTouchedNode;
}

// DataNodeCreated only (root-level add)
export function onNodeCreated(event: DataNodeCreatedEvent, context: TreeSyncContext): void {
  const activeTree = parseActiveTree(context.pathname);
  if (!activeTree) return;

  const templateNodeId = event?.Payload?.TemplateNodeId;
  if (!templateNodeId) return;

  if (!matchesActiveTree(activeTree, event.ProjectId, templateNodeId, event.ProposalId)) return;

  const id = event.Payload?.Id;
  if (!id) return;

  const properties = activeTree.proposalId
    ? selectNodeEventProperties(event.Payload?.Properties, event.Payload?.Proposals, activeTree.proposalId)
    : (event.Payload?.Properties ?? null);
  const node = buildStickyNode(id, null, activeTree.templateNodeId, properties);

  pushStickyTopNode(context.queryClient, activeTree, undefined, node);
}

export function onNodeCreatedWithRelation(nodeEvent: DataNodeCreatedEvent, context: TreeSyncContext): void {
  const activeTree = parseActiveTree(context.pathname);
  if (!activeTree) return;

  const templateNodeId = nodeEvent?.Payload?.TemplateNodeId;
  if (!templateNodeId) return;

  if (!matchesActiveTree(activeTree, nodeEvent.ProjectId, templateNodeId, nodeEvent.ProposalId)) return;

  const id = nodeEvent.Payload?.Id;
  if (!id) return;

  const parentDataNodeId = nodeEvent.Payload.ParentNodeId;
  if (!parentDataNodeId) return;

  const childrenKey = treeQueryKeys.children(activeTree, parentDataNodeId);
  const childrenCacheExists = !!context.queryClient.getQueryData(childrenKey);
  // Keep tracking a parent created this session via sticky-top even while it is collapsed, so
  // that its list stays the gap-free record isNodeCreatedThisSession lets us trust.
  const parentCreatedThisSession = isNodeCreatedThisSession(context.queryClient, activeTree, parentDataNodeId);

  if (childrenCacheExists || parentCreatedThisSession) {
    const properties = activeTree.proposalId
      ? selectNodeEventProperties(nodeEvent.Payload?.Properties, nodeEvent.Payload?.Proposals, activeTree.proposalId)
      : (nodeEvent.Payload?.Properties ?? null);
    const node = buildStickyNode(id, parentDataNodeId, activeTree.templateNodeId, properties);
    pushStickyTopNode(context.queryClient, activeTree, parentDataNodeId, node);
  } else {
    patchHasChildren(context.queryClient, activeTree, parentDataNodeId);
  }
}

// This and onRelationDeleted below each run several full cache scans (patchHasChildren,
// findNodeInCache, removeNodeFromAllStickyTops, removeNodeFromPaginatedPages, ...). A burst of
// relation events (bulk reparent/import) reruns all of them per event with no coalescing. If that
// becomes a real perf issue, batching needs to happen at the SignalR subscription/dispatch layer
// that calls these handlers, not here.
export function onRelationCreated(event: DataRelationCreatedEvent, context: TreeSyncContext): void {
  const resolved = resolveDecompositionRelationSync(event, context);
  if (!resolved) return;

  const { activeTree, childDataNodeId, parentDataNodeId } = resolved;
  if (!shouldLiveRelationOverwriteProposalCache(event, activeTree.proposalId)) return;

  // Always ensure the parent's expand arrow is visible.
  patchHasChildren(context.queryClient, activeTree, parentDataNodeId);

  // Snapshot the child node before any cleanup — we may need it if the
  // parent is expanded and we want to show the child immediately.
  const childNode = findNodeInCache(context.queryClient, activeTree, childDataNodeId);

  const previousParentDataNodeId = childNode?.parentId?.trim();

  // Clean up EVERY prior placement before re-pinning under the new parent:
  //  - all sticky-top lists (root + any parent), so a reparent can't leave the
  //    node pinned under both its old and new parent;
  //  - all paginated pages (roots + children), so it doesn't render as a root
  //    AND a child (the roots list only dedups against the root sticky cache).
  removeNodeFromAllStickyTops(context.queryClient, activeTree, childDataNodeId);
  removeNodeFromPaginatedPages(context.queryClient, activeTree, childDataNodeId);

  // The old parent may have just lost its last child — drop its expand arrow.
  if (previousParentDataNodeId && normalizeId(previousParentDataNodeId) !== normalizeId(parentDataNodeId)) {
    resetHasChildrenIfEmpty(context.queryClient, activeTree, previousParentDataNodeId);
  }

  // Reconcile the destination if its children are already loaded (parent is expanded), or if it
  // was created this session — its sticky-top list is the gap-free record of its children, and
  // must stay that way even while collapsed (see isNodeCreatedThisSession).
  const childrenKey = treeQueryKeys.children(activeTree, parentDataNodeId);
  const childrenCacheExists = context.queryClient.getQueryData(childrenKey) != null;
  if (childrenCacheExists || isNodeCreatedThisSession(context.queryClient, activeTree, parentDataNodeId)) {
    if (childNode) {
      // We have the node's data — pin it optimistically, no refetch needed.
      pushStickyTopNode(context.queryClient, activeTree, parentDataNodeId, {
        ...childNode,
        parentId: parentDataNodeId
      });
    } else if (childrenCacheExists) {
      // The destination has cached children but we have no data for the moved node
      void context.queryClient.invalidateQueries({ queryKey: childrenKey });
    } else {
      // Collapsed destination created this session: nothing is on screen to update, and with no
      // data for the moved node we can't pin it either — so its sticky-top list has just lost a
      // child it will never record. Drop the marker rather than leave a gap behind it, or a later
      // reconcile would read that list as proof of zero children and clear an arrow that belongs
      // to this node (see isNodeCreatedThisSession).
      patchStickyTopNode(context.queryClient, activeTree, parentDataNodeId, { createdThisSession: false });
    }
  }
}

export function onRelationDeleted(event: DataRelationDeletedEvent, context: TreeSyncContext): void {
  const resolved = resolveDecompositionRelationSync(event, context);
  if (!resolved) return;

  const { activeTree, childDataNodeId, parentDataNodeId } = resolved;
  if (!shouldLiveRelationOverwriteProposalCache(event, activeTree.proposalId)) return;

  // Snapshot before detach — otherwise the child is removed from the parent
  // cache and we lose title/subtitle until roots refetch completes.
  const childNode = findNodeInCache(context.queryClient, activeTree, childDataNodeId);

  detachNodeFromParentCache(context.queryClient, activeTree, parentDataNodeId, childDataNodeId);

  // The parent may have just lost its last child — drop its expand arrow.
  resetHasChildrenIfEmpty(context.queryClient, activeTree, parentDataNodeId);

  // Reparent (old relation removed while node is already under a new parent).
  if (isNodePinnedUnderNonRootParent(context.queryClient, activeTree, childDataNodeId)) {
    return;
  }

  if (childNode) {
    pushStickyTopNode(context.queryClient, activeTree, undefined, { ...childNode, parentId: null });
  } else {
    // Node not found in cache — we couldn't pin it via sticky, so
    // fall back to a server refetch to pick up the new root.
    const rootsKey = treeQueryKeys.roots(activeTree);
    void context.queryClient.invalidateQueries({ queryKey: rootsKey, type: 'active' });
  }
}

export function onDataNodeUpdated(event: DataNodeUpdatedEvent, context: TreeSyncContext): void {
  const activeTree = parseActiveTree(context.pathname);
  if (!activeTree) return;

  if (normalizeId(event.ProjectId ?? '') !== activeTree.projectId) return;

  const eventTemplateNodeId = normalizeId(event.Payload?.TemplateNodeId ?? '');
  if (!eventTemplateNodeId || eventTemplateNodeId !== activeTree.templateNodeId) return;
  if (!proposalMatches(normalizeTreeProposalId(event.ProposalId), activeTree.proposalId)) return;

  const nodeId = event.Payload?.Id;
  if (!nodeId) return;

  const rawProperties = event.Payload.Properties;
  if (!activeTree.proposalId && !rawProperties) return;

  const properties = activeTree.proposalId
    ? selectNodeEventProperties(rawProperties, event.Payload.Proposals, activeTree.proposalId)
    : rawProperties;

  const patch = extractNodePatch(activeTree.templateNodeId, properties);
  if (Object.keys(patch).length === 0) return;

  // Directly mutate the node in paginated pages (no refetch).
  patchNodeInPaginatedPages(context.queryClient, activeTree, nodeId, patch);
  patchStickyTopNode(context.queryClient, activeTree, nodeId, patch);
}
